import React, { useState } from "react";
import { FiPhone, FiMail, FiMapPin, FiClock } from "react-icons/fi";

import TextileBackground from "../../components/TextileBackground";
import CinematicSection from "../../components/CinematicSection";
import CustomNavbar from "../../components/Navbar";
import Reveal from "../../components/Reveal";
import FooterLinks from "../../components/FooterLinks";
import Footer from "../../components/Footer";
import company from "../../data/company";
import { useInquiryModal } from "../../context/InquiryModalContext";

const faqs = [
  {
    q: "What types of garments do you manufacture?",
    a: "We manufacture a wide range of apparel, including dresses, trousers, uniforms, ethnic wear, and varieties of menswear and womenswear apparels etc.",
  },
  {
    q: "Do you offer custom garment manufacturing?",
    a: "Yes, we provide custom manufacturing based on your designs, specifications, fabrics, measurements, and branding requirements.",
  },
  {
    q: "What is your minimum order quantity (MOQ)?",
    a: "While there is no specific MOQ to place an order with us, it depends on the garment style, fabric, customization, and production requirements. Contact us to discuss your specific order.",
  },
  {
    q: "Do you provide fabric sourcing?",
    a: "Yes, we can assist with sourcing suitable fabrics, trims, accessories, and other materials based on your requirements.",
  },
  {
    q: "Can you manufacture garments from our own designs?",
    a: "Yes, we can develop and manufacture garments from your sketches, tech packs, samples, or reference garments.",
  },
  {
    q: "Do you provide sampling and prototypes?",
    a: "Yes, we offer sampling services to help you evaluate the fit, construction, fabric, and overall design before bulk production. However, it is chargeable.",
  },
  {
    q: "How do you ensure garment quality?",
    a: "We follow quality control procedures throughout production, including fabric inspection, in line checks, final inspection, and measurement verification.",
  },
  {
    q: "How long does garment production take?",
    a: "Production timelines vary depending on order quantity, garment complexity, fabric availability, and customization. We provide an estimated timeline before production begins.",
  },
  {
    q: "How can I request a quotation?",
    a: "Simply contact us with your garment specifications, estimated quantity, fabric preferences, and delivery requirements, and our team will get in touch with you.",
  },
];

const inputClass =
  "w-full p-3 rounded bg-cream-50 border border-gold-500/50 text-maroon-900 placeholder:text-maroon-900/55 focus:outline-none focus:ring-2 focus:ring-gold-500 font-body text-sm";

export default function ConnectUs() {
  const { openInquiry } = useInquiryModal();
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [contactSent, setContactSent] = useState(false);

  const [feedback, setFeedback] = useState({ name: "", email: "", facility: "Pune", message: "" });
  const [feedbackSent, setFeedbackSent] = useState(false);

  function handleContactSubmit(e) {
    e.preventDefault();
    const body = [`Name: ${contact.name}`, `Email: ${contact.email}`, "", contact.message].join("\n");
    window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(
      "Message from " + (contact.name || "website visitor")
    )}&body=${encodeURIComponent(body)}`;
    setContactSent(true);
  }

  function handleFeedbackSubmit(e) {
    e.preventDefault();
    const body = [
      `Name: ${feedback.name}`,
      `Email: ${feedback.email}`,
      `Facility: ${feedback.facility}`,
      "",
      feedback.message,
    ].join("\n");
    window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(
      "Feedback from " + (feedback.name || "website visitor")
    )}&body=${encodeURIComponent(body)}`;
    setFeedbackSent(true);
  }

  return (
    <TextileBackground>
      <CustomNavbar />

      <CinematicSection zIndex={10} className="bg-cream-100/60" trackHeight="88vh" fitContent>
        <div className="px-6 max-w-4xl mx-auto">
          <header className="text-center mb-10">
            <Reveal>
              <p className="font-accent italic text-maroon-700 text-base tracking-[0.3em] uppercase mb-3">
                Reach Us
              </p>
            </Reveal>
            <Reveal delay={300}>
              <p className="font-body text-maroon-900/90 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mt-4">
                We're here to help — reach out for personalised assistance or share your feedback with us.
              </p>
            </Reveal>
            <Reveal delay={400}>
              <button
                type="button"
                onClick={openInquiry}
                className="inline-block mt-6 px-8 py-3 bg-maroon-700 text-cream-100 font-accent tracking-[0.15em] uppercase text-xs rounded-full shadow-md hover:bg-maroon-800 transition-colors"
              >
                Request a Quote
              </button>
            </Reveal>
          </header>

          <Reveal delay={450}>
            <section className="bg-cream-50 border-2 border-gold-500/40 rounded-2xl p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
                <div>
                  <FiPhone className="mx-auto text-maroon-700 text-xl mb-2" />
                  <p className="font-accent text-maroon-900/85 text-xs">Phone</p>
                  <a href={`tel:${company.phoneRaw}`} className="block font-body text-maroon-700 font-semibold hover:underline">
                    {company.phone}
                  </a>
                  <a href={`tel:${company.phoneAltRaw}`} className="block font-body text-maroon-700 font-semibold hover:underline">
                    {company.phoneAlt}
                  </a>
                </div>
                <div>
                  <FiMail className="mx-auto text-maroon-700 text-xl mb-2" />
                  <p className="font-accent text-maroon-900/85 text-xs">Email</p>
                  <a href={`mailto:${company.email}`} className="font-body text-maroon-700 font-semibold break-words hover:underline">
                    {company.email}
                  </a>
                </div>
                <div>
                  <FiMapPin className="mx-auto text-maroon-700 text-xl mb-2" />
                  <p className="font-accent text-maroon-900/85 text-xs">Office Address</p>
                  <p className="font-body text-maroon-700 font-semibold">{company.address}</p>
                  <a
                    href={company.mapsDirectionsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-block mt-1 font-accent font-medium text-xs sm:text-xs tracking-wide uppercase text-gold-700 hover:underline"
                  >
                    Get Directions
                  </a>
                </div>
                <div>
                  <FiClock className="mx-auto text-maroon-700 text-xl mb-2" />
                  <p className="font-accent text-maroon-900/85 text-xs">Business Hours</p>
                  <p className="font-body text-maroon-700 font-semibold">{company.businessHours}</p>
                </div>
              </div>
            </section>
          </Reveal>
        </div>
      </CinematicSection>

      <CinematicSection zIndex={20} className="bg-cream-100/60" overflowY="auto" trackHeight="114vh" fitContent>
        <div className="max-w-5xl mx-auto px-6 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Reveal>
              <section className="bg-cream-50 p-6 rounded-2xl border-2 border-gold-500/40 h-full">
                <h2 className="font-heading text-maroon-700 text-xl font-medium mb-3">Get in touch</h2>
                <p className="font-body text-maroon-900/85 mb-4 leading-relaxed">Send us a message and our team will get back to you.</p>
                {contactSent ? (
                  <div className="p-4 bg-gold-100/60 border border-gold-500 rounded">
                    <p className="font-body text-maroon-800">
                      Your email app should now be open with this message pre-filled. Please review and hit send from there.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-3">
                    <input
                      value={contact.name}
                      onChange={(e) => setContact({ ...contact, name: e.target.value })}
                      className={inputClass}
                      placeholder="Your name"
                    />
                    <input
                      value={contact.email}
                      onChange={(e) => setContact({ ...contact, email: e.target.value })}
                      className={inputClass}
                      placeholder="Email"
                    />
                    <textarea
                      value={contact.message}
                      onChange={(e) => setContact({ ...contact, message: e.target.value })}
                      className={inputClass}
                      rows="4"
                      placeholder="How can we help?"
                    ></textarea>
                    <button type="submit" className="px-6 py-2.5 bg-maroon-700 text-cream-100 font-accent tracking-wide rounded-full hover:bg-maroon-800 transition-colors">
                      Send message
                    </button>
                  </form>
                )}
              </section>
            </Reveal>

            <Reveal delay={150}>
              <section className="bg-cream-50 p-6 rounded-2xl border-2 border-gold-500/40 h-full">
                <h2 className="font-heading text-maroon-700 text-xl font-medium mb-3">Feedback</h2>
                <p className="font-body text-maroon-900/85 mb-4 leading-relaxed">Tell us about your experience with a specific facility.</p>
                {feedbackSent ? (
                  <div className="p-4 bg-gold-100/60 border border-gold-500 rounded">
                    <p className="font-body text-maroon-800">
                      Your email app should now be open with this feedback pre-filled. Please review and hit send from there.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleFeedbackSubmit} className="space-y-3">
                    <input
                      value={feedback.name}
                      onChange={(e) => setFeedback({ ...feedback, name: e.target.value })}
                      className={inputClass}
                      placeholder="Your name"
                    />
                    <input
                      value={feedback.email}
                      onChange={(e) => setFeedback({ ...feedback, email: e.target.value })}
                      className={inputClass}
                      placeholder="Email"
                    />
                    <select
                      value={feedback.facility}
                      onChange={(e) => setFeedback({ ...feedback, facility: e.target.value })}
                      className={inputClass}
                    >
                      <option>Pune</option>
                    </select>
                    <textarea
                      value={feedback.message}
                      onChange={(e) => setFeedback({ ...feedback, message: e.target.value })}
                      className={inputClass}
                      rows="4"
                      placeholder="Your feedback or inquiry"
                    ></textarea>
                    <button type="submit" className="px-6 py-2.5 bg-maroon-700 text-cream-100 font-accent tracking-wide rounded-full hover:bg-maroon-800 transition-colors">
                      Send feedback
                    </button>
                  </form>
                )}
              </section>
            </Reveal>
          </div>
        </div>
      </CinematicSection>

      <CinematicSection zIndex={30} className="bg-cream-100/60" fitContent staticFlow>
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-heading text-maroon-700 text-xl font-medium mb-4 text-center">FAQs</h2>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <Reveal key={i} delay={i * 120}>
                <details className="bg-cream-50 p-4 rounded-xl border border-gold-500/40">
                  <summary className="cursor-pointer font-accent text-maroon-700 font-medium text-base">{f.q}</summary>
                  <div className="mt-2 font-body text-maroon-900/90 text-sm leading-relaxed">{f.a}</div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </CinematicSection>

      <FooterLinks />
      <Footer />
    </TextileBackground>
  );
}
